import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Dropdown, DropdownMenuItem, DropdownMenuDivider } from '@bibliocommons/base-dropdown';
import { checkedOutItemShape, bibShape } from '@bibliocommons/bc-prop-types';
import { TransactionalSolidButton } from '@bibliocommons/base-buttons';
import { Link } from '@bibliocommons/base-links';
import Context from '@bibliocommons/context';

import ReturnDigitalCheckoutsModal from 'app/components/shared/ItemTransactions/components/digital-checkouts/ReturnDigitalCheckoutsModal';
import accountShape from 'app/shapes/accountShape';
import { checkoutTransactionsShape } from 'app/shapes/borrowing';
import DownloadButton from './DownloadButton';
import DownloadModal from './DownloadModal';
import FulfilmentButton from './FulfilmentButton';
import FulfilmentModal from './FulfilmentModal';
import DigitalReturnButton from './DigitalReturnButton';
import ThirdPartyReaderLink from './ThirdPartyReaderLink';

import './CheckedOutDropdown.scss';

function renderViewAllOption() {
  return (
    <DropdownMenuItem>
      <Link href="/v2/checkedout" dataKey="view-all-checkedout">
        <FormattedMessage id="view_all_checked_out" />
      </Link>
    </DropdownMenuItem>
  );
}

export default class CheckedOutDropdown extends React.PureComponent {
  getModalKey(name) {
    return `${name}-modal-${this.props.bib.get('id')}`;
  }

  fulfilmentWorkFlowEnabled() {
    return this.props.bib.getIn(['policy', 'provider']) === 'OverDriveAPI';
  }

  renderDownloadModal() {
    const {
      digitalFormats,
      bib,
      checkoutItem,
      isLoading,
      checkoutTransactions,
      currentAccount,
      bibsActions,
      checkoutActions,
      modalActions
    } = this.props;

    const materialPolicy = bib.getIn(['policy', 'materialPolicy'], Immutable.Map());
    if (!this.fulfilmentWorkFlowEnabled() && materialPolicy.get('supportsDownload')) {
      return (
        <DownloadModal
          bib={bib}
          modalKey={this.getModalKey('download')}
          bibsActions={bibsActions}
          modalActions={modalActions}
          currentAccount={currentAccount}
          checkoutItem={checkoutItem}
          checkoutTransactions={checkoutTransactions}
          checkoutActions={checkoutActions}
          digitalFormats={digitalFormats}
          isLoading={isLoading}
        />
      );
    }

    return null;
  }

  renderReturnDigitalCheckoutsModal() {
    const { checkoutActions, checkoutItem, checkoutTransactions, currentAccount, bib } = this.props;
    const isReturning = checkoutTransactions.getIn([checkoutItem.get('metadataId'), 'isReturning'], false);
    const checkInEnabled = checkoutItem.get('checkInEnabled');
    const metadataId = checkoutItem.get('metadataId');
    const checkoutId = checkoutItem.get('checkoutId');
    const accountId = currentAccount.get('id');

    if (bib && checkInEnabled) {
      return (
        <ReturnDigitalCheckoutsModal
          bib={bib}
          modalKey={this.getModalKey('return')}
          modalActions={this.props.modalActions}
          shelfActions={this.props.shelfActions}
          isLoading={isReturning}
          checkIn={checkoutActions.digitalCheckIn}
          metadataId={metadataId}
          checkoutId={checkoutId}
          accountId={accountId}
        />
      );
    }

    return null;
  }

  renderFulfilmentModal() {
    const { bib, checkoutItem, currentAccount, checkoutActions, modalActions } = this.props;

    return this.fulfilmentWorkFlowEnabled() ? (
      <FulfilmentModal
        bib={bib}
        modalKey={this.getModalKey('fulfilment')}
        accountId={currentAccount.get('id')}
        checkoutItem={checkoutItem}
        checkoutActions={checkoutActions}
        modalActions={modalActions}
      />
    ) : null;
  }

  renderDigitalReturnOption() {
    const { checkoutItem, modalActions } = this.props;

    return (
      checkoutItem.get('checkInEnabled') && (
        <DropdownMenuItem>
          <DigitalReturnButton modalKey={this.getModalKey('return')} modalActions={modalActions} />
        </DropdownMenuItem>
      )
    );
  }

  renderThirdPartyReaderOption() {
    const { checkoutItem } = this.props;

    return (
      !this.fulfilmentWorkFlowEnabled() &&
      checkoutItem.get('thirdPartyReaderUrl') && (
        <DropdownMenuItem>
          <ThirdPartyReaderLink checkoutItem={checkoutItem} />
        </DropdownMenuItem>
      )
    );
  }

  renderDownloadOption() {
    const { bib, checkoutItem, modalActions, digitalFormats, trackCheckoutConsumeDownload } = this.props;

    return !this.fulfilmentWorkFlowEnabled() && digitalFormats.some(format => format.get('hasDownloadLink')) ? (
      <DropdownMenuItem>
        <DownloadButton
          bib={bib}
          modalActions={modalActions}
          modalKey={this.getModalKey('download')}
          checkoutItem={checkoutItem}
          trackCheckoutConsumeDownload={trackCheckoutConsumeDownload}
        />
      </DropdownMenuItem>
    ) : null;
  }

  renderFulfilmentOption() {
    const { bib, checkoutItem, modalActions, trackCheckoutConsumeAccess } = this.props;
    const bibId = bib.get('id');

    return this.fulfilmentWorkFlowEnabled() ? (
      <DropdownMenuItem>
        <FulfilmentButton
          modalKey={this.getModalKey('fulfilment')}
          modalActions={modalActions}
          trackCheckoutConsumeAccess={trackCheckoutConsumeAccess}
          bibId={bibId}
          econtentId={checkoutItem.get('metadataId')}
          format={checkoutItem.get('format')}
          title={bib.getIn(['briefInfo', 'title'])}
        />
      </DropdownMenuItem>
    ) : null;
  }

  render() {
    return (
      <div className="cp-checkedout-dropdown">
        <Dropdown
          block
          right
          className="checked-out-dropdown"
          dataKey="checked-out-dropdown"
          buttonTag={TransactionalSolidButton}
          renderLabel={() => <FormattedMessage id="checked_out" />}
        >
          {this.renderFulfilmentOption()}
          {this.renderDownloadOption()}
          {this.renderThirdPartyReaderOption()}
          {this.renderDigitalReturnOption()}
          <DropdownMenuDivider />
          {renderViewAllOption()}
        </Dropdown>
        {this.renderDownloadModal()}
        {this.renderReturnDigitalCheckoutsModal()}
        {this.renderFulfilmentModal()}
      </div>
    );
  }
}

CheckedOutDropdown.propTypes = {
  checkoutItem: checkedOutItemShape.isRequired,
  currentAccount: accountShape.isRequired,
  checkoutTransactions: checkoutTransactionsShape.isRequired,
  bib: bibShape.isRequired,
  isLoading: PropTypes.bool,
  digitalFormats: ImmutablePropTypes.list,
  bibsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  checkoutActions: PropTypes.objectOf(PropTypes.func).isRequired,
  shelfActions: PropTypes.objectOf(PropTypes.func).isRequired,
  modalActions: PropTypes.objectOf(PropTypes.func).isRequired,
  trackCheckoutConsumeAccess: PropTypes.func,
  trackCheckoutConsumeDownload: PropTypes.func
};

CheckedOutDropdown.contextType = Context;
